// @flow @format
import React, {PureComponent} from 'react';
import CloseIcon from '../Icon/angle-right';
import ChartIcon from '../Icon/pie-chart';
import ArrowLeft from '../Icon/angle-left';
import {ActiveDownload} from './Download.js';
import Modal from '../Modal';
import classNames from 'lib/classnames';
import {toSIString, toPercentage} from 'lib/util';
import './DownloadHistory.css';

import type {DownloadSummary} from 'lib/downloader';

type DownloadListProps = {
	title: string,
	downloads: DownloadSummary[],
	throughput: number,
	actions: {
		deleteDownload: (id: string) => void,
		pauseDownload: (id: string) => void,
		startDownload: (id: string) => void,
		saveDownload: (id: string) => void,
	},
};

const DownloadList = ({
	title,
	downloads,
	actions,
	throughput,
}: DownloadListProps) => (
	<div className="DownloadList">
		<h3 className="DownloadList__Title">{title}</h3>
		{downloads.map(download => (
			<ActiveDownload
				key={download.id}
				download={download}
				actions={actions}
				throughput={throughput}
			/>
		))}
	</div>
);

type Props = {
	visible: boolean,
	throughput: number,
	activeFileDownloads: DownloadSummary[],
	finishedFileDownloads: DownloadSummary[],
	activeFolderDownloads: DownloadSummary[],
	finishedFolderDownloads: DownloadSummary[],
	usedBytes: number,
	grantedBytes: number,
	toggleHistory: () => void,
	deleteDownload: (id: string) => void,
	pauseDownload: (id: string) => void,
	startDownload: (id: string) => void,
	saveDownload: (id: string) => void,
};

type State = {
	confirmation: ?{
		prev: DownloadSummary,
		next: DownloadSummary,
	},
};

class DownloadHistory extends PureComponent<Props, State> {
	state = {
		confirmation: null,
	};

	componentWillReceiveProps(nextProps: Props) {
		if (this.state.confirmation) {
			let prev: DownloadSummary, next: DownloadSummary;

			for (const download of nextProps.activeFolderDownloads) {
				if (download.id === this.state.confirmation.prev.id) {
					prev = download;
					continue;
				}

				if (download.id === this.state.confirmation.next.id) {
					next = download;
				}
			}

			if (prev && next) {
				this.setState({confirmation: {prev, next}});
			}
		}
	}

	_startFolderDownload = (id: string) => {
		let prev: DownloadSummary, next: DownloadSummary;

		for (const download of this.props.activeFolderDownloads) {
			if (download.initialized) {
				prev = download;
			}

			if (download.id === id) {
				next = download;
			}
		}

		if (!prev || !next) {
			this.props.startDownload(id);
			return;
		}

		if (prev && next) {
			if (prev.id === next.id) {
				this.props.startDownload(id);
				return;
			}

			this.setState({
				confirmation: {prev, next},
			});
		}
	};

	_closeConfirmation = () => {
		this.setState({confirmation: null});
	};

	render() {
		const {
			usedBytes,
			grantedBytes,
			throughput,
			activeFileDownloads,
			finishedFileDownloads,
			activeFolderDownloads,
			finishedFolderDownloads,
		} = this.props;

		const fileActions = {
			startDownload: this.props.startDownload,
			pauseDownload: this.props.pauseDownload,
			deleteDownload: this.props.deleteDownload,
			saveDownload: this.props.saveDownload,
		};

		const folderActions = {
			startDownload: this._startFolderDownload,
			pauseDownload: this.props.pauseDownload,
			deleteDownload: this.props.deleteDownload,
			saveDownload: this.props.saveDownload,
		};

		return (
			<div className="DownloadHistory">
				<div
					className={classNames({
						DownloadHistory__Toggle: true,
						'DownloadHistory__Toggle--Visible': !this.props.visible,
					})}
					onClick={this.props.toggleHistory}
				>
					{!this.props.visible && (
						<ArrowLeft className="DownloadHistory__Toggle__Icon" />
					)}
				</div>

				<div className="DownloadHistory__Header">
					<div
						className="DownloadHistory__Header__IconBox"
						onClick={this.props.toggleHistory}
					>
						<CloseIcon />
					</div>

					<h2 className="DownloadHistory__Header__Title">
						Descargas
					</h2>
				</div>

				<div className="DownloadHistory__Body">
					<div
						style={{
							textAlign: 'right',
							padding: 'calc(var(--baseline) / 2)',
							fontSize: 'var(--md-text)',
							backgroundColor:
								'var(--background-secondary-color)',
							color: 'var(--color-gray-dark)',
							borderBottom: '1px solid var(--divider-color)',
						}}
					>
						<ChartIcon /> {toSIString(usedBytes)} /{' '}
						{toSIString(grantedBytes)}
					</div>

					{activeFileDownloads.length === 0 &&
						finishedFileDownloads.length === 0 &&
						activeFolderDownloads.length === 0 &&
						finishedFolderDownloads.length === 0 && (
							<div className="DownloadHistory__NoDownloads">
								<svg
									id="Layer_1"
									x="0px"
									y="0px"
									viewBox="0 0 496 496"
									style={{
										enableBackground: 'new 0 0 496 496',
										width: 150,
										margin: 'calc(var(--baseline) * 1)',
									}}
								>
									<path
										fill="#333333"
										d="M423.824,113.432C420.4,50.304,367.96,0,304,0c-43.056,0-82.08,22.648-103.576,59.592  C193.888,57.2,187.056,56,180,56c-20.52,0-39.224,10.288-50.232,27.256C121.32,81.096,112.688,80,104,80C46.656,80,0,126.656,0,184  c0,51,36.456,94.136,86.68,102.56l2.64-15.784C46.832,263.656,16,227.16,16,184c0-48.52,39.48-88,88-88  c9.168,0,18.288,1.448,27.104,4.296l6.52,2.12l3.088-6.136C148.248,81.304,163.304,72,180,72c7.104,0,13.912,1.672,20.248,4.968  l7.2,3.752l3.64-7.248C228.888,38.016,264.496,16,304,16c57.344,0,104,46.656,104.016,103.504l-0.04,8.152l7.208,0.712  C452.144,132.024,480,162.824,480,200c0,39.704-32.296,72-72,72v16c48.52,0,88-39.48,88-88  C496,156.952,465.44,120.904,423.824,113.432z"
									/>
									<path
										fill="#1BC3A5"
										d="M416,424H80V248c0-13.232,10.768-24,24-24h288c13.232,0,24,10.768,24,24V424z"
									/>
									<g>
										<path
											fill="#333333"
											d="M80,432c0,13.232,10.768,24,24,24h80v24h-24v16h176v-16h-24v-24h80c13.232,0,24-10.768,24-24v-24H80   V432z M296,480h-96v-24h96V480z M96,424h304v8c0,4.416-3.584,8-8,8H104c-4.416,0-8-3.584-8-8V424z"
										/>
										<path
											fill="#333333"
											d="M225.84,138.672l-3.688-15.56C171.432,135.12,136,179.888,136,232   c0,45.296,26.792,85.024,67.112,102.528l-29.64,9.88l5.064,15.168l54.336-18.112l-8.944-62.608l-15.84,2.264l5.776,40.44   C176.912,307.6,152,272.408,152,232C152,187.344,182.368,148.968,225.84,138.672z"
										/>
										<path
											fill="#333333"
											d="M317.472,104.416l-54.336,18.112l8.944,62.608l15.84-2.264l-5.776-40.44   C319.088,156.4,344,191.592,344,232c0,44.656-30.368,83.032-73.84,93.328l3.688,15.56C324.568,328.88,360,284.112,360,232   c0-45.296-26.792-85.024-67.112-102.528l29.64-9.88L317.472,104.416z"
										/>
									</g>
								</svg>
								<p className="DownloadHistory__NoDownloads__Text">
									No hay descargas pendientes
								</p>
							</div>
						)}
					{activeFileDownloads.length > 0 && (
						<DownloadList
							title="Descargas de archivos activas"
							downloads={activeFileDownloads}
							actions={fileActions}
							throughput={throughput}
						/>
					)}
					{activeFolderDownloads.length > 0 && (
						<DownloadList
							title="Descargas de directorios activas"
							downloads={activeFolderDownloads}
							actions={folderActions}
							throughput={throughput}
						/>
					)}
					{finishedFileDownloads.length > 0 && (
						<DownloadList
							title="Historial de descarga de archivos"
							downloads={finishedFileDownloads}
							actions={fileActions}
							throughput={throughput}
						/>
					)}
					{finishedFolderDownloads.length > 0 && (
						<DownloadList
							title="Historial de descarga de directorios"
							downloads={finishedFolderDownloads}
							actions={folderActions}
							throughput={throughput}
						/>
					)}
				</div>

				<Modal
					onClose={this._closeConfirmation}
					visible={!!this.state.confirmation}
				>
					<DownloadConfirmation
						confirmation={this.state.confirmation}
						onSuccess={id =>
							this.props.startDownload(id) &&
							this._closeConfirmation()}
						onCancel={this._closeConfirmation}
					/>
				</Modal>
			</div>
		);
	}
}

export default DownloadHistory;

const DownloadConfirmation = ({
	onSuccess,
	onCancel,
	confirmation,
}: {
	onSuccess: (id: string) => void,
	onCancel: () => void,
	confirmation: ?{next: DownloadSummary, prev: DownloadSummary},
}) => {
	if (!confirmation) {
		return null;
	}

	const {next, prev} = confirmation;

	return (
		<div className="DownloadConfirmation">
			<h2>Advertencia</h2>

			<h4>Solo es posible descargar un directorio a la vez.</h4>

			<p>
				Si desea iniciar la descarga de <b>{next.filename}</b>, debe
				cancelar su descarga actual.
			</p>

			<p>
				Si cancela <b>{prev.filename}</b>, se perderá su progreso de{' '}
				{toSIString(prev.downloaded)} / {toSIString(prev.size)}
				<b> ({toPercentage(prev.downloaded, prev.size)})</b>
			</p>

			<h4>¿Desea continuar?</h4>

			<div className="DownloadConfirmation__Buttons">
				<button
					className="DownloadConfirmation__Button"
					onClick={() => onCancel()}
				>
					Cancelar
				</button>
				<button
					className="DownloadConfirmation__Button"
					onClick={() => onSuccess(next.id)}
				>
					Aceptar
				</button>
			</div>
		</div>
	);
};



// WEBPACK FOOTER //
// ./src/components/DownloadHistory/DownloadHistory.js